import { useCallback, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { api } from './api/client';
import { Sidebar } from './components/Sidebar';
import { StatusBanner } from './components/StatusBanner';
import { FilterBar } from './components/FilterBar';
import { FilterProvider } from './context/FilterContext';
import type { Banner, PolicyArea } from './types';
import { DashboardPage } from './pages/DashboardPage';
import { ScenarioBuilderPage } from './pages/ScenarioBuilderPage';
import { AreaExplorerPage } from './pages/AreaExplorerPage';
import { DecompositionPage } from './pages/DecompositionPage';
import { InteractionsLagsPage } from './pages/InteractionsLagsPage';
import { VettingPage } from './pages/VettingPage';
import { MacroOutcomesPage } from './pages/MacroOutcomesPage';
import { EvidencePage } from './pages/EvidencePage';
import { VariableWizardPage } from './pages/VariableWizardPage';
import { LinkageBuilderPage } from './pages/LinkageBuilderPage';
import { SubModelBuilderPage } from './pages/SubModelBuilderPage';
import { ActionsPage } from './pages/ActionsPage';
import { ProjectionControlPage } from './pages/ProjectionControlPage';
import { ModelLibraryPage } from './pages/ModelLibraryPage';
import { DependencyGraphPage } from './pages/DependencyGraphPage';
import { VerificationLayerPage } from './pages/VerificationLayerPage';
import { TrendViewPage } from './pages/TrendViewPage';
import { EquilibriumPage } from './pages/EquilibriumPage';
import { PerceptionPage } from './pages/PerceptionPage';
import { StochasticMacroPage } from './pages/StochasticMacroPage';
import { extApi } from './api/extensibilityClient';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LoginPage } from './pages/LoginPage';
import { AccountPage } from './pages/AccountPage';
import { ModelOverviewPage } from './pages/ModelOverviewPage';
import { TargetTracerPage } from './pages/TargetTracerPage';
import { ChiefEconomistPage } from './pages/ChiefEconomistPage';
import { DecisionsPage } from './pages/DecisionsPage';
import { ProgrammesPage } from './pages/ProgrammesPage';
import { WorkbookModelPage } from './pages/WorkbookModelPage';
import { ModelEquationsPage } from './pages/ModelEquationsPage';
import { ScenarioDetailPage } from './pages/ScenarioDetailPage';
import { AssumptionsPage } from './pages/AssumptionsPage';
import { EditorProvider } from './context/EditorContext';
import { ShockBanner } from './components/ShockBanner';
import { ShocksPage } from './pages/ShocksPage';
import { ExecutivePage } from './pages/ExecutivePage';
import { useFilters } from './context/FilterContext';

function AppShell() {
  const [banner, setBanner] = useState<Banner | null>(null);
  const [areas, setAreas] = useState<PolicyArea[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [verification, setVerification] = useState<any>(null);
  const [shockKey, setShockKey] = useState(0);
  const { filters } = useFilters();
  const { user } = useAuth();

  const refreshBanner = useCallback(() => {
    Promise.all([api.banner(), extApi.verification()])
      .then(([b, v]) => { setBanner(b); setVerification(v); })
      .catch((e) => setError(String(e)));
  }, []);

  useEffect(() => {
    Promise.all([api.banner(), api.policyAreas(), extApi.verification()])
      .then(([b, a, v]) => {
        setBanner(b);
        setAreas(a);
        setVerification(v);
      })
      .catch((e) => setError(String(e)));
  }, []);

  const onScenarioChange = () => refreshBanner();

  const contextLabel = filters.variableId
    ? `Indicator: ${filters.variableId}`
    : filters.policyAreaId
      ? `Area: ${areas.find((a) => a.id === filters.policyAreaId)?.name || filters.policyAreaId}`
      : null;

  const verificationStatus = verification?.macro?.[0]?.verification_status
    || (verification?.overall_attainment_pct != null
      ? `Overall scenario attainment: ${(verification.overall_attainment_pct * 100).toFixed(0)}%`
      : 'Between Incremental and Moderate');

  if (error) {
    return (
      <div className="app-shell">
        <div className="error" style={{ margin: '2rem' }}>
          Failed to connect to API. Start the backend with: uvicorn backend.main:app --reload --port 8000
          <pre style={{ marginTop: '1rem', fontSize: '0.85rem' }}>{error}</pre>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <ShockBanner refreshKey={shockKey} />
      {banner && (
        <StatusBanner
          banner={banner}
          contextLabel={contextLabel}
          verificationStatus={verificationStatus}
          attainmentPct={verification?.overall_attainment_pct}
        />
      )}
      <div className="layout-body">
        <Sidebar areas={areas} user={user} />
        <main className="main-content">
          <FilterBar />
          <Routes>
            <Route path="/" element={<DashboardPage onScenarioChange={onScenarioChange} />} />
            <Route path="/executive" element={<ExecutivePage />} />
            <Route path="/scenario-builder" element={<ScenarioBuilderPage onScenarioChange={onScenarioChange} />} />
            <Route path="/areas/:areaId" element={<AreaExplorerPage />} />
            <Route path="/decomposition" element={<DecompositionPage />} />
            <Route path="/interactions-lags" element={<InteractionsLagsPage />} />
            <Route path="/vetting" element={<VettingPage />} />
            <Route path="/macro-outcomes" element={<MacroOutcomesPage />} />
            <Route path="/equilibrium" element={<EquilibriumPage />} />
            <Route path="/perception" element={<PerceptionPage />} />
            <Route path="/stochastic" element={<StochasticMacroPage />} />
            <Route path="/evidence" element={<EvidencePage onDataChange={refreshBanner} />} />
            <Route path="/projection-control" element={<ProjectionControlPage onChange={onScenarioChange} />} />
            <Route path="/verification" element={<VerificationLayerPage />} />
            <Route path="/variables/new" element={<VariableWizardPage />} />
            <Route path="/linkage-builder" element={<LinkageBuilderPage />} />
            <Route path="/sub-model-builder" element={<SubModelBuilderPage />} />
            <Route path="/actions" element={<ActionsPage />} />
            <Route path="/model-library" element={<ModelLibraryPage />} />
            <Route path="/dependency-graph" element={<DependencyGraphPage />} />
            <Route path="/trends/:variableId" element={<TrendViewPage />} />
            <Route path="/model-overview" element={<ModelOverviewPage />} />
            <Route path="/target-tracer" element={<TargetTracerPage />} />
            <Route path="/chief-economist" element={<ChiefEconomistPage />} />
            <Route path="/decisions" element={<DecisionsPage />} />
            <Route path="/signature-programmes" element={<ProgrammesPage />} />
            <Route path="/workbook-model" element={<WorkbookModelPage />} />
            <Route path="/model-equations" element={<ModelEquationsPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/scenarios/:scenarioId" element={<ScenarioDetailPage />} />
            <Route path="/assumptions" element={<AssumptionsPage />} />
            <Route path="/shocks" element={<ShocksPage onChange={() => setShockKey((k) => k + 1)} />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function AuthGate() {
  const { ready, authRequired, token } = useAuth();
  if (!ready) return <div className="loading" style={{ padding: '2rem' }}>Loading…</div>;
  if (authRequired && !token) return <LoginPage />;
  return (
    <FilterProvider>
      <EditorProvider>
        <AppShell />
      </EditorProvider>
    </FilterProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}
